import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "../components/seo";
import { ButtonLink, Callout, CheckList, PageHero, Section, SectionHeading } from "../components/ui";
import { services } from "../site-data";
import NotFound from "./NotFound";

export function ServicesOverview() {
  return (
    <>
      <Seo title="ICT-diensten voor MKB in het Gooi en Almere | STAQL" description="ICT-diensten voor MKB in Hilversum, Blaricum, Naarden, Huizen en Almere. Van data en AI tot automatisering, software en slimme systemen." />
      <PageHero eyebrow="Diensten" title="Van digitaal vraagstuk naar werkende oplossing." intro="STAQL helpt wanneer data onoverzichtelijk is, processen onnodig veel handwerk vragen of een ambitie technisch moet worden gerealiseerd. We combineren advies en uitvoering, zodat het niet bij een plan blijft.">
        <ButtonLink to="/contact" showArrow>Bespreek je vraagstuk</ButtonLink>
      </PageHero>
      <Section className="section--tint">
        <div className="service-listing">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <article className="service-row" key={service.slug}>
                <div className="service-row__index">0{index + 1}</div>
                <div className="service-row__icon"><Icon aria-hidden="true" /></div>
                <div className="service-row__copy">
                  <span className="eyebrow">{service.shortTitle}</span>
                  <h2>{service.cardTitle}</h2>
                  <p>{service.description}</p>
                  <CheckList items={service.capabilities.slice(0, 3).map((item) => item.title)} />
                </div>
                <Link className="circle-link" to={`/diensten/${service.slug}`} aria-label={`Bekijk ${service.shortTitle}`}><ArrowRight aria-hidden="true" /></Link>
              </article>
            );
          })}
        </div>
      </Section>
      <Section>
        <div className="quality-block">
          <div className="quality-block__icon"><ShieldCheck aria-hidden="true" /></div>
          <div><span className="eyebrow">Overal ingebouwd</span><h2>Veilig en beheersbaar vanaf het begin.</h2></div>
          <p>Security en privacy worden niet achteraf toegevoegd. Bij ieder project kijken we naar toegang, data, afhankelijkheden en beheer. De zwaarte van maatregelen stemmen we af op het risico en de praktijk van jouw organisatie.</p>
        </div>
      </Section>
      <Section className="section--compact">
        <div className="split-content">
          <SectionHeading eyebrow="Projectvorm" title="Duidelijkheid over aanpak en resultaat" />
          <div><p>STAQL werkt bij voorkeur op projectbasis: een helder vraagstuk, een afgesproken aanpak en een concreet resultaat. Voor verkenning, beheer of werk dat vooraf moeilijk af te bakenen is, kan een andere vorm passender zijn. Dat bespreken we vooraf.</p></div>
        </div>
      </Section>
      <Callout title="Weet je nog niet onder welke dienst je vraag valt?" text="Dat hoeft ook niet. Beschrijf wat er beter moet; STAQL helpt de juiste route bepalen." />
    </>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((item) => item.slug === slug);
  if (!service) return <NotFound />;

  return (
    <>
      <Seo title={`${service.shortTitle} in het Gooi en Almere | STAQL`} description={service.intro} />
      <PageHero eyebrow={service.eyebrow} title={service.title} intro={service.intro}>
        <ButtonLink to="/contact" showArrow>Bespreek je vraag</ButtonLink>
      </PageHero>
      <Section>
        <div className="split-content split-content--wide">
          <SectionHeading eyebrow="Herkenning" title="Herken je dit?" />
          <CheckList items={service.recognition} />
        </div>
      </Section>
      <Section className="section--tint">
        <SectionHeading eyebrow="Mogelijkheden" title="Van analyse tot werkende toepassing" text="STAQL kijkt naar de hele keten en kiest technologie pas wanneer duidelijk is wat die moet verbeteren." />
        <div className="capability-grid">
          {service.capabilities.map((capability) => {
            const Icon = capability.icon;
            return <article key={capability.title}><span><Icon aria-hidden="true" /></span><h3>{capability.title}</h3><p>{capability.text}</p></article>;
          })}
        </div>
      </Section>
      <Section>
        <div className="outcome-panel">
          <div><span className="eyebrow">Resultaat</span><h2>Wat het kan opleveren</h2></div>
          <CheckList items={service.outcomes} />
        </div>
      </Section>
      <Section className="section--compact">
        <div className="approach-band">
          <span className="eyebrow">Aanpak</span>
          <h2>{service.approachTitle}</h2>
          <p>{service.approach}</p>
        </div>
      </Section>
      <Callout title={service.ctaTitle} text={service.ctaText} />
    </>
  );
}
